import { RRule, type Options as RRuleOptions } from 'rrule';
import type { MonthlyOn } from '@/modules/meetings/types';
import { wallClockInTzToUTC } from '@/shared/lib/date-utils';

// Everything below the marker is mirrored verbatim into
// `supabase/functions/_shared/meeting-schedules.ts`, which the materializer
// imports: Deno resolves neither the path alias nor the bare npm specifier
// above. This copy is canonical — regenerate the mirror with
// `bun run sync:expander`, which `meeting-schedules-mirror.test.ts` checks.

// ─── shared body ────────────────────────────────────────────────────────────

/**
 * A recurring series: an RFC 5545 rule, expanded in the workspace's zone.
 *
 * Expansion is wall-clock-first. A weekly 09:00 meeting is 09:00 on both sides
 * of a daylight-saving boundary, which is what a team means by "Mondays at
 * nine" and what expanding in UTC would quietly break.
 */

/** The shapes the schedule editor can produce. Storage is wider; the editor is not. */
export type ScheduleRule =
  | { freq: 'weekly'; interval: number; byWeekday: number[] }
  | ({ freq: 'monthly'; interval: number } & MonthlyOn)
  | { freq: 'yearly'; month: number; dayOfMonth: number };

export interface ScheduleSpec {
  rule: ScheduleRule;
  /** Last calendar date an occurrence may fall on, in the effective zone. */
  until?: string;
}

export interface MeetingScheduleRow {
  dtstart: string;
  rrule: string;
  exdates: string[];
}

/**
 * The effective zone for every expansion, resolved here and nowhere else.
 *
 * A `meeting_schedules.timezone` column would be a copy of a value it must
 * always equal, and a stale copy of an IANA string does not error — it produces
 * meetings at the wrong hour. When a per-schedule override is genuinely wanted,
 * it lands as `schedule.timezone ?? settings.timezone` inside this function and
 * nowhere else.
 */
export function effectiveTimezone(settings: { timezone?: string | null } | null | undefined): string {
  return settings?.timezone || 'UTC';
}

// `dayOfWeek` is `Date.getDay()` throughout the product — 0 is Sunday. RRULE
// counts from Monday.
const RRULE_WEEKDAYS = [RRule.SU, RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA];
const jsDayToRRule = (day: number) => (day + 6) % 7;
const rruleDayToJs = (day: number) => (day + 1) % 7;

/**
 * "The 31st" in a month that has no 31st means the 30th, not nothing.
 *
 * RFC 5545 drops a `BYMONTHDAY` that a month cannot satisfy, so `BYMONTHDAY=31`
 * skips February entirely. The product clamps instead, and has since #409. The
 * two are reconciled rather than diverging: a window of candidate days with
 * `BYSETPOS=-1` takes the last day at or before the anchor, which is clamping
 * expressed in the interchange format itself. An exported calendar then agrees
 * with the app rather than dropping months from it.
 */
function monthDayParts(dayOfMonth: number): Pick<Partial<RRuleOptions>, 'bymonthday' | 'bysetpos'> {
  const day = Math.min(Math.max(1, dayOfMonth), 31);
  if (day <= 28) return { bymonthday: [day] };
  const candidates: number[] = [];
  for (let d = 28; d <= day; d += 1) candidates.push(d);
  return { bymonthday: candidates, bysetpos: [-1] };
}

function ruleOptions(spec: ScheduleSpec): Partial<RRuleOptions> {
  const { rule } = spec;
  if (rule.freq === 'weekly') {
    return {
      freq: RRule.WEEKLY,
      interval: rule.interval,
      byweekday: rule.byWeekday.map((d) => RRULE_WEEKDAYS[d]),
    };
  }
  if (rule.freq === 'monthly') {
    const base = { freq: RRule.MONTHLY, interval: rule.interval };
    if (rule.on === 'day') return { ...base, ...monthDayParts(rule.dayOfMonth) };
    // Every month holds at least four of each weekday, so `nth` up to 4 always
    // resolves and only `-1` needs to mean "the last one".
    return { ...base, byweekday: [RRULE_WEEKDAYS[rule.dayOfWeek].nth(rule.nth)] };
  }
  return { freq: RRule.YEARLY, bymonth: [rule.month + 1], ...monthDayParts(rule.dayOfMonth) };
}

/**
 * The RRULE text for a rule. No DTSTART line: that is its own column, and an
 * instant rather than a wall clock.
 */
export function buildRRule(spec: ScheduleSpec): string {
  const options = ruleOptions(spec);
  if (spec.until) {
    // UNTIL is a date here, not an instant: "through the 20th" is what a team
    // says, and the last second of that day is what it means.
    options.until = new Date(`${spec.until}T23:59:59Z`);
  }
  return RRule.optionsToString(options).replace(/^RRULE:/, '');
}

/**
 * The editor state a stored rule came from.
 *
 * Every rule `buildRRule` produces parses back into the state that produced it.
 * A rule from outside that set — a hand-written import, a future editor — has
 * no editor state and returns null rather than a lossy approximation.
 */
export function parseRRule(rrule: string): ScheduleSpec | null {
  let options: Partial<RRuleOptions>;
  try {
    options = RRule.parseString(rrule.startsWith('RRULE:') ? rrule : `RRULE:${rrule}`);
  } catch {
    return null;
  }

  const until = options.until
    ? new Date(options.until).toISOString().slice(0, 10)
    : undefined;
  const interval = options.interval ?? 1;
  const weekdays = options.byweekday
    ? (Array.isArray(options.byweekday) ? options.byweekday : [options.byweekday])
    : [];

  if (options.freq === RRule.WEEKLY) {
    const byWeekday = weekdays.map((w) => rruleDayToJs(typeof w === 'number' ? w : (w as { weekday: number }).weekday));
    if (!byWeekday.length) return null;
    return { rule: { freq: 'weekly', interval, byWeekday: byWeekday.sort((a, b) => a - b) }, until };
  }

  const anchor = monthlyAnchorFromOptions(options, weekdays);
  if (!anchor) return null;

  if (options.freq === RRule.MONTHLY) {
    return { rule: { freq: 'monthly', interval, ...anchor }, until };
  }
  if (options.freq === RRule.YEARLY) {
    const month = Array.isArray(options.bymonth) ? options.bymonth[0] : options.bymonth;
    if (!month || anchor.on !== 'day') return null;
    return { rule: { freq: 'yearly', month: month - 1, dayOfMonth: anchor.dayOfMonth }, until };
  }
  return null;
}

function monthlyAnchorFromOptions(
  options: Partial<RRuleOptions>,
  weekdays: unknown[],
): MonthlyOn | null {
  const nthWeekday = weekdays.find((w) => typeof w === 'object' && w !== null && 'n' in w) as
    | { weekday: number; n: number }
    | undefined;
  if (nthWeekday) {
    return { on: 'weekday', nth: nthWeekday.n as 1 | 2 | 3 | 4 | -1, dayOfWeek: rruleDayToJs(nthWeekday.weekday) };
  }
  const monthdays = Array.isArray(options.bymonthday)
    ? options.bymonthday
    : options.bymonthday != null ? [options.bymonthday] : [];
  if (!monthdays.length) return null;
  // The clamping window collapses back to the anchor it was built from: its
  // largest candidate is the day the team asked for.
  return { on: 'day', dayOfMonth: Math.max(...monthdays) };
}

// ─── Expansion ──────────────────────────────────────────────────────────────

const MS_PER_DAY = 86_400_000;

/** The wall clock an instant shows in a zone, as 'YYYY-MM-DD' and 'HH:MM'. */
function wallClockIn(tz: string, instant: Date): { date: string; time: string } {
  const date = new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(instant);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(instant);
  const hour = String(Number(parts.find((p) => p.type === 'hour')?.value ?? '0') % 24).padStart(2, '0');
  const minute = parts.find((p) => p.type === 'minute')?.value ?? '00';
  return { date, time: `${hour}:${minute}` };
}

/** A wall clock held as a Date whose UTC fields *are* the wall clock. */
function floating(date: string, time: string): Date {
  return new Date(`${date}T${time}:00Z`);
}

function floatingParts(d: Date): { date: string; time: string } {
  return { date: d.toISOString().slice(0, 10), time: d.toISOString().slice(11, 16) };
}

/**
 * Every occurrence of a schedule whose instant falls in `[from, to]`.
 *
 * The rule is expanded against wall clock, then each result is placed back on
 * the timeline in `tz`. Expanding against instants instead would drift an hour
 * at each daylight-saving boundary.
 */
export function expandOccurrences(
  schedule: MeetingScheduleRow,
  tz: string,
  range: { from: string; to: string },
): string[] {
  const spec = parseRRule(schedule.rrule);
  if (!spec) return [];

  const start = wallClockIn(tz, new Date(schedule.dtstart));
  const rule = new RRule({
    ...ruleOptions(spec),
    dtstart: floating(start.date, start.time),
    ...(spec.until ? { until: floating(spec.until, '23:59') } : {}),
  });

  // The floating window is widened by a day at each end before the real
  // instants are filtered below, so that an occurrence whose zone offset pushes
  // it across the boundary is still a candidate.
  const fromWall = wallClockIn(tz, new Date(range.from));
  const toWall = wallClockIn(tz, new Date(range.to));
  const candidates = rule.between(
    new Date(floating(fromWall.date, fromWall.time).getTime() - MS_PER_DAY),
    new Date(floating(toWall.date, toWall.time).getTime() + MS_PER_DAY),
    true,
  );

  const excluded = new Set(schedule.exdates.map((e) => new Date(e).getTime()));
  const fromMs = new Date(range.from).getTime();
  const toMs = new Date(range.to).getTime();

  const seen = new Set<number>();
  const out: string[] = [];
  for (const candidate of candidates) {
    const { date, time } = floatingParts(candidate);
    const instant = wallClockInTzToUTC(date, time, tz);
    const ms = new Date(instant).getTime();
    if (ms < fromMs || ms > toMs) continue;
    if (excluded.has(ms) || seen.has(ms)) continue;
    seen.add(ms);
    out.push(instant);
  }
  return out;
}

/**
 * The first occurrence at or after `after`, whatever the window.
 *
 * A quarterly or annual series is almost never inside the materialization
 * window, and a team that cannot see its next planning session has no way to
 * prepare for it.
 */
export function nextOccurrence(
  schedule: MeetingScheduleRow,
  tz: string,
  after: string,
): string | null {
  const spec = parseRRule(schedule.rrule);
  if (!spec) return null;
  const horizon = spec.until
    ? new Date(`${spec.until}T23:59:59Z`).toISOString()
    : new Date(new Date(after).getTime() + 366 * 2 * MS_PER_DAY).toISOString();
  if (new Date(horizon).getTime() < new Date(after).getTime()) return null;
  return expandOccurrences(schedule, tz, { from: after, to: horizon })[0] ?? null;
}

/**
 * The instant a schedule's first occurrence sits at, for a rule the team just
 * described in wall clock.
 */
export function dtstartFor(startDate: string, time: string, tz: string): string {
  return wallClockInTzToUTC(startDate, time, tz);
}
